/* rk-video-carousel: markup + CSS in snippets/rk-video-carousel.liquid (task 08c, replaces Tolstoy).
   Like Tolstoy's spotlight: a strip of slots around the centre, filled with the videos repeated as needed so
   both edges always show a tile. A move slides every slot one place; the slot that leaves one end is
   refilled off-screen and parked at the other end, so nothing ever pops. Only the centre video plays,
   and only while the carousel is on screen. Swipe / drag / arrows / keys move exactly one tile.
   Order = block order; the first block starts in the centre. */
if (!customElements.get('rk-video-carousel')) {
  customElements.define('rk-video-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.waiting || this.track) return;
      this.waiting = true;
      // Everything (slot rebuild, layout reads, poster downloads) waits until the carousel is within 600px of the
      // viewport: it sits far below the hero, and doing it at load competed with the LCP image.
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        this.init();
      }, { rootMargin: '600px 0px' });
      io.observe(this);
    }

    init() {
      this.querySelectorAll('video[data-poster]').forEach((v) => { v.poster = v.dataset.poster; });
      this.track = this.querySelector('.rk-vc__track');
      this.items = [...this.track.children].filter((el) => el.classList.contains('rk-vc__slide'));
      this.items.forEach((el) => el.remove());
      this.n = this.items.length;
      this.current = 0;
      this.sound = false;
      this.inView = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.reduced;
      this.slots = [];

      this.querySelector('.rk-vc__arrow--prev').addEventListener('click', () => this.step(-1));
      this.querySelector('.rk-vc__arrow--next').addEventListener('click', () => this.step(1));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });
      this.track.addEventListener('click', (e) => {
        if (this.dragged) return;
        if (e.target.closest('.rk-vc__mute')) { this.toggleSound(); return; }
        const slot = this.slots.find((s) => s.el.contains(e.target));
        if (!slot) return;
        if (slot.pos !== 0) { this.step(slot.pos); return; }
        this.userPaused = !this.userPaused;
        this.sync();
      });
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
      this.bindSwipe();

      new ResizeObserver(() => { if (this.track.clientWidth !== this.builtWidth) this.build(); }).observe(this.track);
      new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting; this.sync(); }, { threshold: 0.25 }).observe(this.track);
      // Start downloading the centre video (then its neighbours) well before the carousel scrolls into view,
      // so it plays the moment it appears instead of after a cold fetch.
      this.near = false;
      new IntersectionObserver(([entry]) => { this.near = entry.isIntersecting; this.sync(); }, { rootMargin: '600px 0px' }).observe(this.track);
      this.build();
      this.classList.add('is-ready');
    }

    item(i) {
      return ((i % this.n) + this.n) % this.n;
    }

    makeSlot(pos) {
      const index = this.item(this.current + pos);
      const el = this.items[index].cloneNode(true);
      el.setAttribute('aria-label', `${index + 1} of ${this.n}`);
      el.querySelector('video').muted = !this.sound;
      const mute = el.querySelector('.rk-vc__mute');
      mute.setAttribute('aria-label', this.sound ? 'Mute video' : 'Unmute video');
      mute.setAttribute('aria-pressed', String(this.sound));
      return { el, pos };
    }

    build() {
      const width = this.track.clientWidth;
      if (!width) return;
      this.builtWidth = width;
      this.slots.forEach((s) => s.el.remove());
      this.track.appendChild(this.items[0].cloneNode(true));
      this.slotW = this.track.lastElementChild.offsetWidth;
      this.track.lastElementChild.remove();
      const visible = Math.ceil(width / 2 / this.slotW + 0.5) - 1;
      this.reach = visible + 2;
      this.slots = [];
      for (let pos = -this.reach; pos <= this.reach; pos++) {
        const s = this.makeSlot(pos);
        this.slots.push(s);
        this.track.appendChild(s.el);
      }
      this.place(true);
      this.sync();
    }

    place(instant) {
      const width = this.track.clientWidth;
      const centre = (width - this.slotW) / 2;
      this.slots.forEach((s) => {
        if (instant || s.jump) s.el.style.transition = 'none';
        const x = centre + s.pos * this.slotW;
        s.el.style.transform = `translateX(${x}px)`;
        s.el.classList.toggle('is-centre', s.pos === 0);
        if (instant || s.jump) { s.el.offsetWidth; s.el.style.transition = ''; s.jump = false; }
        const hidden = s.pos !== 0 && (x < 0 || x + this.slotW > width);
        s.el.setAttribute('aria-hidden', String(hidden));
        s.el.querySelector('.rk-vc__mute').tabIndex = hidden ? -1 : 0;
      });
    }

    step(by) {
      if (!by || !this.slots.length) return;
      const span = this.slots.length;
      this.current = this.item(this.current + by);
      this.slots.forEach((s, i) => {
        s.pos -= by;
        if (Math.abs(s.pos) > this.reach) {
          const pos = s.pos - Math.sign(s.pos) * span;
          const fresh = this.makeSlot(pos);
          fresh.jump = true;
          s.el.replaceWith(fresh.el);
          this.slots[i] = fresh;
        }
      });
      this.slots.sort((a, b) => a.pos - b.pos);
      this.userPaused = this.reduced;
      this.place(false);
      this.sync();
    }

    toggleSound() {
      this.sound = !this.sound;
      this.classList.toggle('is-sound', this.sound);
      this.querySelectorAll('.rk-vc__mute').forEach((b) => {
        b.setAttribute('aria-label', this.sound ? 'Mute video' : 'Unmute video');
        b.setAttribute('aria-pressed', String(this.sound));
      });
      if (this.sound) this.userPaused = false;
      this.sync();
    }

    warm(video) {
      if (video.preload !== 'auto') video.preload = 'auto';
    }

    sync() {
      this.slots.forEach((s) => {
        const video = s.el.querySelector('video');
        video.muted = !this.sound;
        if (s.pos === 0 && (this.near || this.inView)) {
          this.warm(video);
          // once the centre video has data, fetch the tiles one step either side so a swipe starts instantly
          if (!this.warmed && video.readyState < 3) {
            this.warmed = true;
            const nb = () => this.slots.forEach((n) => { if (Math.abs(n.pos) === 1) this.warm(n.el.querySelector('video')); });
            video.addEventListener('canplay', nb, { once: true });
          } else if (video.readyState >= 3) {
            this.slots.forEach((n) => { if (Math.abs(n.pos) === 1) this.warm(n.el.querySelector('video')); });
          }
        }
        if (s.pos === 0 && this.inView && !this.userPaused) {
          const p = video.play();
          if (p) p.catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    bindSwipe() {
      let x0 = null;
      let y0 = null;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('.rk-vc__mute')) return;
        x0 = e.clientX;
        y0 = e.clientY;
        this.dragged = false;
      });
      this.track.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
          this.dragged = true;
          this.step(dx < 0 ? 1 : -1);
          setTimeout(() => { this.dragged = false; }, 50);
        }
      });
      this.track.addEventListener('pointercancel', () => { x0 = null; });
    }
  });
}
